import {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    IRetryPolicy,
    LogLevel,
    RetryContext,
} from "@microsoft/signalr";

import { ApiService } from "@/services/api-service";
import AuthService from "@/services/auth-service";
import { useDataSources } from "./data-sources";
import { getMessageHeaders } from "@/utils/utils";
import { useInterval } from "@/hooks/use-interval";
import { handleAndNotifyError } from "@/utils/errorHandling";

const debug: boolean = false;

export type SignalRHub = Pick<
    HubConnection,
    "on" | "off" | "start" | "stop" | "state" | "invoke"
>;

interface ISignalRHubContext {
    setSignalRHub: (service: HubConnection | undefined) => void;
    signalRHub?: SignalRHub;
}

export const SignalRHubContext = createContext<ISignalRHubContext>({
    setSignalRHub: () => {
        console.warn("No context setSignalRHub handler has been set.");
    },
    signalRHub: undefined,
});

export const useSignalRHub = () => use(SignalRHubContext);

/**
 * SignalRHub context provider.
 *
 * @param options
 * @returns
 */
export const SignalRHubProvider: FunctionComponent<PropsWithChildren> = (
    options,
): ReactElement => {
    const { dataSourceService } = useDataSources();
    const [signalRHub, setSignalRHub] = useState<HubConnection | undefined>(
        undefined,
    );

    const joinedDeviceCountRef = useRef<Record<string, number>>({}); // the count represents the number of subscriptions to a device at any time

    /**
     * Helper to update the device subscription count
     * @param deviceString The device identifier (e.g., "device_someDeviceIdentity")
     * @param direction Increment or decrement the count
     */
    const updateDeviceCount = (
        deviceString: string,
        direction: "increment" | "decrement",
    ) => {
        const previousValue = joinedDeviceCountRef.current[deviceString] ?? 0;
        joinedDeviceCountRef.current = {
            ...joinedDeviceCountRef.current,
            [deviceString]:
                direction === "increment"
                    ? previousValue + 1
                    : previousValue - 1,
        };
    };

    /**
     * Wait for the SignalR connection to be in the Connected state.
     * @param timeoutMs Maximum time to wait in milliseconds (default: 10000)
     * @returns Promise that resolves when connected, or rejects on timeout/disconnection
     */
    const waitForConnection = useCallback(
        async (timeoutMs: number = 10000): Promise<void> => {
            if (!signalRHub) {
                throw new Error("SignalR hub is not initialized");
            }

            if (signalRHub.state === HubConnectionState.Connected) {
                return;
            }

            if (
                signalRHub.state === HubConnectionState.Disconnected ||
                signalRHub.state === HubConnectionState.Disconnecting
            ) {
                throw new Error(
                    `Cannot invoke: connection is ${signalRHub.state}`,
                );
            }

            // Wait for connection to be ready (it's probably in Connecting or Reconnecting state)
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(
                        new Error(
                            `Timeout waiting for SignalR connection (state: ${signalRHub.state})`,
                        ),
                    );
                }, timeoutMs);

                const checkConnection = () => {
                    if (signalRHub.state === HubConnectionState.Connected) {
                        clearTimeout(timeout);
                        resolve();
                    } else if (
                        signalRHub.state === HubConnectionState.Disconnected
                    ) {
                        clearTimeout(timeout);
                        reject(
                            new Error("Connection disconnected while waiting"),
                        );
                    } else {
                        // Still connecting/reconnecting, check again shortly
                        setTimeout(checkConnection, 100);
                    }
                };

                checkConnection();
            });
        },
        [signalRHub],
    );

    /**
     * Overriding invoke and adding a garbage collection to resolve this issue:
     * https://jetcharge.atlassian.net/browse/IL-2918
     * We want to decouple the joinGroup and leave group calls so that calls to both in quick
     * succession don't end up in a race condition on the backend. We also want to keep track
     * of subscriptions so that we don't unsubscribe when there are still components listening.
     */
    const invoke: HubConnection["invoke"] = useCallback(
        async (methodName, ...props) => {
            if (!signalRHub) {
                return Promise.reject(
                    new Error("SignalR hub is not initialized"),
                );
            }
            if (methodName === "leaveGroup") {
                const deviceString = props[0] as string;
                updateDeviceCount(deviceString, "decrement");
                // Don't invoke it; it will be cleaned up in the garbageCollection
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return Promise.resolve(undefined as any); // Super hacky, but we don't use the return value anywhere so it's safe enough
            }
            try {
                await waitForConnection();

                const result = await signalRHub.invoke(methodName, ...props);
                if (methodName === "joinGroup" && props[0]) {
                    const deviceString = props[0] as string;
                    updateDeviceCount(deviceString, "increment");
                }
                return result;
            } catch (error) {
                console.error(
                    `SignalR invocation failed for method "${methodName}":`,
                    error,
                );
                throw error;
            }
        },
        [signalRHub, waitForConnection],
    );

    const garbageCollection = () => {
        const deviceStringsToLeave = Object.entries(
            joinedDeviceCountRef.current,
        )
            .filter(([, count]) => count <= 0)
            .map(([deviceString]) => deviceString);
        for (const deviceString of deviceStringsToLeave) {
            signalRHub?.invoke("leaveGroup", deviceString);
            delete joinedDeviceCountRef.current?.[deviceString];
        }
    };

    useInterval(garbageCollection, 10000);

    const { instance: msalInstance, inProgress: msalInProgress } = useMsal();

    const msalAccountExists = !!msalInstance.getActiveAccount();

    useEffect(() => {
        if (debug) {
            console.log(
                `SignalRHubProvider joinSignalR invoked with msal ${msalInProgress} account ${msalInstance.getActiveAccount()?.username}`,
            );
        }

        async function joinSignalRAsync() {
            if (
                msalInProgress === InteractionStatus.None &&
                msalAccountExists
            ) {
                const retryPolicy: IRetryPolicy = {
                    nextRetryDelayInMilliseconds: (
                        retryContext: RetryContext,
                    ) => {
                        if (debug) {
                            console.debug(retryContext);
                        }

                        if (retryContext.previousRetryCount === 0) {
                            return 0;
                        } else if (retryContext.previousRetryCount <= 3) {
                            return 100;
                        } else if (retryContext.previousRetryCount <= 5) {
                            return 1000;
                        } else {
                            return 10000;
                        }
                    },
                };

                try {
                    const apiConfig =
                        await ApiService.getApiConfigAsync(msalInstance);

                    // Builds the SignalR connection
                    const hubConnection = new HubConnectionBuilder()
                        .withUrl(apiConfig.basePath, {
                            headers: getMessageHeaders(),
                            accessTokenFactory: async () => {
                                const accessToken =
                                    await AuthService.getAuthorizationToken(
                                        msalInstance,
                                    );
                                return accessToken;
                            },
                        })
                        .configureLogging(LogLevel.Information)
                        .withAutomaticReconnect(retryPolicy)
                        .build();

                    // Starts the SignalR connection
                    if (debug) {
                        console.log(`SignalRHubProvider hub start`);
                    }

                    await hubConnection.start();

                    // Update the data source service with a reference to the current SignalR hub
                    //(This could be done as a data source constructor param like msal, but,
                    // We don't need/want data source service to rebuild whenever signalR reconnects)
                    // eslint-disable-next-line react-hooks/immutability
                    dataSourceService!.signalRHub = hubConnection;

                    setSignalRHub(hubConnection);
                } catch (error) {
                    handleAndNotifyError(
                        error,
                        "Failed to connect to websocket",
                    );
                }
            }
        }

        joinSignalRAsync();

        return () => {
            // Cleanup

            if (debug) {
                console.log(`SignalRHubProvider cleanup`);
            }

            setSignalRHub((prevHub) => {
                if (debug) {
                    console.log(
                        `SignalRHubProvider cleanup setSignalRHub called`,
                        prevHub,
                    );
                }

                if (prevHub && prevHub.state === HubConnectionState.Connected) {
                    if (debug) {
                        console.log(
                            `SignalRHubProvider cleanup disconnecting previous signalR`,
                        );
                    }
                    prevHub.stop();
                }

                return undefined;
            });
        };
    }, [msalInstance, msalInProgress, msalAccountExists, dataSourceService]);

    const memoisedHub = useMemo(() => {
        return signalRHub
            ? {
                  invoke,
                  on: signalRHub.on.bind(signalRHub),
                  off: signalRHub.off.bind(signalRHub),
                  start: signalRHub.start.bind(signalRHub),
                  stop: signalRHub.stop.bind(signalRHub),
                  // This needs to be a live getter, can't just use
                  // signalRHub.state as the value becomes stale
                  get state() {
                      return signalRHub?.state;
                  },
              }
            : undefined;
    }, [invoke, signalRHub]);

    return (
        <>
            <SignalRHubContext
                value={{
                    signalRHub: memoisedHub,
                    setSignalRHub,
                }}
            >
                {options.children}
            </SignalRHubContext>
        </>
    );
};
